import { fileToBase64, MAX_FILE_SIZE, UPLOAD_ACCEPT } from "@/components/screenata/vault-document/UploadDocumentDialog";
import { useState, useEffect, useRef, useCallback } from 'react'
import { z } from 'zod/v3'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { MainToolbar } from '@/tiptap/editor/components/main-toolbar'
import type {
  VaultDocLockedByTestDTO,
  VaultDocSuggestedTestDTO,
  VaultDocumentCategory,
  VaultDocumentStatus,
} from '@deepguide-ai/types'
import { Badge } from '@deepguide-ai/ui/badge'
import { Button } from '@deepguide-ai/ui/button'
import { SearchableSelect } from '@deepguide-ai/ui/combobox'
import { Input } from '@deepguide-ai/ui/input'
import { Label } from '@deepguide-ai/ui/label'
import { Textarea } from '@deepguide-ai/ui/textarea'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@deepguide-ai/ui/dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@deepguide-ai/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@deepguide-ai/ui/select'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@deepguide-ai/ui/sheet'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@deepguide-ai/ui/tooltip'
import {
  EditorContext,
  type Editor,
  type EditorContextValue,
} from '@tiptap/react'
import { useSetAtom } from 'jotai'
import {
  MoreHorizontal,
  Pencil,
  Save,
  Trash2,
  Archive,
  Bot,
  Upload,
  FileText,
  Loader2,
  X,
} from 'lucide-react'
import { toast } from 'sonner'
import { screenataPageContextAtom } from '@/stores/screenataPageContextStore'
import { usePageHeaderTitle } from '@/lib/page-header'
import { trpc } from '@/lib/trpc'
import {
  VAULT_CATEGORY_LABELS,
  VAULT_STATUS_LABELS,
  VAULT_SOURCE_LABELS,
} from '@/lib/vault-document.constants'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import {
  DetailAsideSection,
  DetailPanelAside,
  DetailPanelLayout,
  DetailPanelMain,
} from '@/components/compliance/detail-primitives'
import { FilePreview, getFileType } from '@/components/evidence-viewer'
import { ImageEditorDialogHost } from '@/components/image-editor/image-editor-dialog-host'
import {
  PageHeaderActions,
  PageHeaderBreadcrumb,
  PageHeaderExtra,
} from '@/components/layout/PageHeaderActions'
import { FrameworkRefChips } from '@/components/screenata/FrameworkRefChips'
import { DocumentRelatedTest } from '@/components/screenata/vault-document/DocumentRelatedTest'
import { WorkspaceWriteGate } from '@/components/permissions'
import UploadFile from '@/components/UploadFile'
import { PolicyContentViewer, hasRenderablePolicyContent } from '@/components/screenata/PolicyContentViewer'
import {
  KeyDocumentPreparation,
  KeyDocumentSources,
} from '@/components/screenata/vault-document/KeyDocumentsPanel'
import {
  VaultDocumentEditor,
  readEditorContent,
} from '@/components/screenata/vault-document/VaultDocumentEditor'
import { DocumentReviewHistory } from '@/components/screenata/vault-document/DocumentReviewHistory'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/documents_/$docId'
)({
  component: VaultDocumentDetailPage,
  validateSearch: z.object({
    keyDocument: z.string().optional(),
    testId: z.string().trim().min(1).optional(),
    documentIds: z.array(z.string()).optional(),
    versionId: z.string().optional(),
  }),
  staticData: { pageHeader: { title: 'Document', pad: false, scroll: false } },
})

function formatDate(date: Date | string | null | undefined): string {
  if (!date) return '—'
  const d = typeof date === 'string' ? new Date(date) : date
  if (Number.isNaN(d.getTime())) return '—'
  return d.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  })
}

function formatLockTooltip(_lockedByTests: VaultDocLockedByTestDTO[]): string {
  return 'Historical evidence uses this unversioned document. Create a separate revision to edit; archiving preserves the original.'
}

function DocumentStatusBadge({ status }: { status: VaultDocumentStatus }) {
  const className =
    status === 'ACTIVE'
      ? 'border-status-green/30 bg-status-green-bg text-status-green'
      : 'border-border-surface bg-muted text-muted-foreground'

  return (
    <span
      className={`font-body inline-flex h-5 items-center rounded-sm border px-2 text-[11px] font-medium ${className}`}
    >
      {VAULT_STATUS_LABELS[status] ?? status}
    </span>
  )
}

// ── Uploaded File Viewer ──
// Thin wrapper around the shared evidence-viewer FilePreview so vault docs
// get the same rich rendering as evidence files in the auditor surfaces:
// markdown / docx / xlsx / text-json / pdf with page nav / images with
// fullscreen / video. Without this, .md and .docx uploads fell into the
// generic download-only fallback.

function UploadedFileViewer({
  docId,
  fileName,
  fileMimeType,
  versionId,
}: {
  versionId?: string
  docId: string
  fileName?: string | null
  fileMimeType?: string | null
}) {
  const fileUrlQuery = useQuery({
    ...trpc.vaultDocument.getFileUrl.queryOptions({ id: docId, versionId }),
    staleTime: 30 * 60 * 1000,
  })

  if (fileUrlQuery.isPending) {
    return (
      <div className='border-border flex h-[700px] items-center justify-center rounded-sm border bg-card'>
        <Loader2 className='h-5 w-5 animate-spin text-status-blue' />
      </div>
    )
  }

  const url = fileUrlQuery.data?.url
  if (!url) {
    return (
      <div className='border-border text-text-subtle flex h-[300px] items-center justify-center rounded-sm border bg-card text-[13px]'>
        File unavailable.
      </div>
    )
  }

  const displayName = fileName ?? 'Document'
  const fileType = getFileType(fileMimeType ?? '', displayName)

  return (
    <div
      className={
        fileType === 'pdf' ||
        fileType === 'docx' ||
        fileType === 'markdown' ||
        /\.(json|txt)$/i.test(displayName)
          ? 'min-h-0 flex-1 overflow-hidden rounded-sm bg-card shadow-sm [&>div>div]:border-0'
          : fileType === 'image'
            ? 'min-h-0 flex-1 overflow-hidden bg-transparent [&>div>div]:border-0'
          : 'overflow-hidden rounded-sm bg-card shadow-sm [&>div>div]:border-0'
      }
    >
      <FilePreview
        file={{ url, filename: displayName }}
        type={fileType}
      />
    </div>
  )
}

// ── Main Component ──

function VaultDocumentDetailPage() {
  const { workspaceId, docId } = Route.useParams()
  const { keyDocument, documentIds, versionId, testId } = Route.useSearch()
  const { canWorkspaceWrite } = useWorkspacePermissions(workspaceId)
  return (
    <SharedVaultDocumentDetailPage
      key={`${docId}:${versionId ?? 'draft'}`}
      workspaceId={workspaceId}
      docId={docId}
      readOnly={!canWorkspaceWrite || !!versionId}
      keyDocumentKey={keyDocument}
      testId={testId}
      versionId={versionId}
      documentIds={documentIds}
    />
  )
}

export function SharedVaultDocumentDetailPage({
  workspaceId,
  docId,
  panelMode = false,
  readOnly = false,
  keyDocumentKey,
  testId,
  documentIds,
  versionId,
}: {
  workspaceId: string
  docId: string
  panelMode?: boolean
  readOnly?: boolean
  keyDocumentKey?: string
  testId?: string
  documentIds?: string[]
  versionId?: string
}) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const [isEditing, setIsEditing] = useState(false)
  const [replacementFile, setReplacementFile] = useState<File | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [editCategory, setEditCategory] =
    useState<VaultDocumentCategory>('PROCEDURE')
  const [editDescription, setEditDescription] = useState('')
  const [showDetailsDialog, setShowDetailsDialog] = useState(false)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  // TipTap editor ref for capturing content on save
  const editorRef = useRef<Editor | null>(null)
  const [editor, setEditor] = useState<Editor | null>(null)

  // Query
  const docQuery = useQuery({
    ...trpc.vaultDocument.getById.queryOptions({ id: docId }),
    enabled: !!docId,
  })
  const versionQuery = useQuery({
    ...trpc.vaultDocument.getVersion.queryOptions({
      id: docId,
      versionId: versionId ?? '',
    }),
    enabled: !!versionId,
  })
  const versionsQuery = useQuery({
    ...trpc.vaultDocument.versions.queryOptions({ id: docId }),
    enabled: !!docId,
  })
  const keyDocumentsQuery = useQuery(
    trpc.vaultDocument.keyDocuments.queryOptions()
  )
  const keyEntry = keyDocumentKey
    ? keyDocumentsQuery.data?.find((d) => d.key === keyDocumentKey)
    : undefined
  const currentVersion = versionsQuery.data?.find(v => v.isCurrent)
  const doc = versionId
    ? versionQuery.data && docQuery.data
      ? { ...docQuery.data, ...versionQuery.data,
          ...(versionQuery.data.documentMetadata as { description?: string | null; category?: VaultDocumentCategory; tags?: string[] } | null ?? { description: null, tags: [] }),
          id: docId, isLocked: true, updatedAt: versionQuery.data.capturedAt, reviewedAt: null }
      : undefined
    : docQuery.data

  const suggestionsQuery = useQuery({
    ...trpc.vaultDocument.listSuggestions.queryOptions({ id: docId }),
    enabled: !!docId && !keyDocumentKey,
  })
  const suggestedTests = suggestionsQuery.data ?? []

  const dismissSuggestionMutation = useMutation(
    trpc.compliance.rejectVaultDocumentEvidenceSuggestion.mutationOptions()
  )
  const handleDismissSuggestion = async (statusId: string) => {
    await dismissSuggestionMutation.mutateAsync({
      statusId,
      vaultDocumentId: docId,
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.listSuggestions.queryKey({ id: docId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.list.queryKey(),
    })
  }

  // Mutations
  const replaceFileMutation = useMutation(trpc.vaultDocument.uploadFile.mutationOptions())
  const updateMutation = useMutation(
    trpc.vaultDocument.update.mutationOptions()
  )
  const selectMaterialsMutation = useMutation(
    trpc.vaultDocument.selectKeyDocumentMaterials.mutationOptions()
  )
  const deleteMutation = useMutation(
    trpc.vaultDocument.delete.mutationOptions()
  )

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: trpc.vaultDocument.getFileUrl.queryKey({ id: docId }) })
    queryClient.invalidateQueries({ queryKey: trpc.vaultDocument.versions.queryKey({ id: docId }) })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.getById.queryKey({ id: docId }),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.list.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.getStats.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.vaultDocument.keyDocuments.queryKey(),
    })
    queryClient.invalidateQueries({
      queryKey: trpc.complianceProgram.getGuidedPath.queryKey(),
    })
  }

  const startEditing = () => {
    if (!doc) return
    if (doc.isLocked || versionId) {
      toast.error(
        'Historical evidence uses this unversioned document. Create a separate revision to preserve the original.'
      )
      return
    }
    editorRef.current = null
    setEditor(null)
    setReplacementFile(null)
    setIsEditing(true)
  }

  const startEditingDetails = () => {
    if (!doc) return
    if (doc.isLocked || versionId) {
      toast.error(
        'Historical evidence uses this unversioned document. Create a separate revision to preserve the original.'
      )
      return
    }
    setEditTitle(doc.title)
    setEditCategory(doc.category)
    setEditDescription(doc.description ?? '')
    setShowDetailsDialog(true)
  }

  const cancelEditing = () => {
    setIsEditing(false)
    setReplacementFile(null)
    editorRef.current = null
    setEditor(null)
  }

  const handleEditorCreate = useCallback(({ editor }: { editor: Editor }) => {
    editorRef.current = editor
    setEditor(editor)
  }, [])

  const handleSave = async () => {
    if (!doc) return
    const isUploadedFile = Boolean(doc.fileKey && doc.sourceType === 'UPLOADED')
    if (isUploadedFile ? !replacementFile : !editorRef.current) return

    try {
      if (isUploadedFile && replacementFile) {
        await replaceFileMutation.mutateAsync({
          documentId: docId,
          filename: replacementFile.name,
          contentType: replacementFile.type || 'application/octet-stream',
          base64Data: await fileToBase64(replacementFile),
          size: replacementFile.size,
          title: doc.title,
          category: doc.category,
          description: doc.description,
        })
      } else {
        await updateMutation.mutateAsync({
          id: docId,
          data: readEditorContent(editorRef.current),
        })
      }
      if (keyEntry) {
        await selectMaterialsMutation.mutateAsync({
          key: keyEntry.key,
          documentIds: documentIds?.length ? documentIds : [docId],
        })
      }
      toast.success(keyEntry ? 'Document saved and linked. Tests have not been submitted.' : 'Document saved')
      setIsEditing(false)
      editorRef.current = null
      setEditor(null)
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Could not save the document.')
    } finally {
      invalidate()
      queryClient.invalidateQueries({ queryKey: trpc.evidenceDocLink.list.queryKey() })
      queryClient.invalidateQueries({ queryKey: trpc.cesTool.resolve.queryKey() })
    }
  }

  const handleDetailsSave = async () => {
    if (!doc || !editTitle.trim()) return
    try {
      await updateMutation.mutateAsync({
        id: docId,
        data: {
          title: editTitle.trim(),
          category: editCategory,
          description: editDescription.trim() || null,
        },
      })
      toast.success('Document details updated')
      setShowDetailsDialog(false)
      invalidate()
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : 'Could not update document details.'
      )
    }
  }

  const handleStatusChange = async (status: VaultDocumentStatus) => {
    await updateMutation.mutateAsync({ id: docId, data: { status } })
    toast.success(`Status updated to ${VAULT_STATUS_LABELS[status]}`)
    invalidate()
  }

  const handleDelete = async () => {
    await deleteMutation.mutateAsync({ id: docId })
    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.keyDocuments.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.list.queryKey(),
      }),
      queryClient.invalidateQueries({
        queryKey: trpc.vaultDocument.getStats.queryKey(),
      }),
    ])
    toast.success('Document deleted')
    navigate({
      to: '/workspaces/$workspaceId/documents',
      params: { workspaceId },
      search: {
        tab: keyDocumentKey ? 'key' : 'all',
        keyDocument: keyDocumentKey,
        testId,
      },
      replace: true,
    })
  }

  // Page context
  const setPageContext = useSetAtom(screenataPageContextAtom)
  useEffect(() => {
    if (keyEntry) {
      setPageContext({
        page: 'key-document',
        summary: keyEntry.title,
        data: {
          keyDocumentKey: keyEntry.key,
          ...(doc ? { documentId: doc.id, title: doc.title } : {}),
        },
      })
    } else if (doc) {
      setPageContext({
        page: 'document-detail',
        summary: `Viewing document: ${doc.title} (${VAULT_CATEGORY_LABELS[doc.category]}, ${VAULT_STATUS_LABELS[doc.status]})`,
        data: { documentId: doc.id, title: doc.title },
      })
    }
    return () => setPageContext(null)
  }, [doc, keyEntry, setPageContext])

  usePageHeaderTitle(doc?.title ?? keyEntry?.title)

  const documentBreadcrumb = testId ? (
    <Link
      to='/workspaces/$workspaceId/controls'
      params={{ workspaceId }}
      search={{ test: testId }}
      className='font-heading text-text-subtle hover:text-foreground text-[15px] font-medium'
    >
      Back to Test
    </Link>
  ) : (
    <Link
      to='/workspaces/$workspaceId/documents'
      params={{ workspaceId }}
      search={!docId ? { tab: 'key' } : undefined}
      preload={false}
      className={docId ? 'text-text-subtle hover:text-text-secondary text-[13px] transition-colors' : undefined}
    >
      Documents
    </Link>
  )

  if (docQuery.isLoading || (versionId && versionQuery.isPending)) {
    return (
      <DetailPanelLayout>
        <DetailPanelMain
          className='bg-muted'
          viewClassName='px-0 pt-0 pb-0'
          contentClassName='h-full max-w-none pb-0'
        >
          <div className='bg-muted flex min-h-full justify-center px-20 py-10'>
            <div className='border-border-divider flex min-h-[calc(var(--app-vh)_-_170px)] w-full max-w-[816px] items-center justify-center rounded-sm border border-dashed bg-card px-16 py-12'>
              <div className='mx-auto max-w-[460px] text-center'>
                <Loader2 className='text-primary mx-auto size-10 animate-spin' />
                <h2 className='font-heading text-foreground mt-4 text-[20px] leading-[1.25] font-semibold tracking-[-0.01em]'>
                  {keyEntry?.title ?? doc?.title ?? 'Document'}
                </h2>
                <p className='text-muted-foreground mt-2 text-[13px] leading-5'>
                  Loading document…
                </p>
              </div>
            </div>
          </div>
        </DetailPanelMain>
      </DetailPanelLayout>
    )
  }

  if (!docId && keyEntry) {
    return (
      <>
        <PageHeaderBreadcrumb>
          {documentBreadcrumb}
        </PageHeaderBreadcrumb>
        <DetailPanelLayout>
          <DetailPanelMain
            className='bg-surface-subtle'
            viewClassName='px-0 pt-0 pb-0 [&>div]:h-full'
            contentClassName='h-full max-w-none pb-0'
          >
            <KeyDocumentSources
              entry={keyEntry}
              testId={testId}
              workspaceId={workspaceId}
              readOnly={readOnly}
            />
          </DetailPanelMain>
          <DetailPanelAside>
            <DetailAsideSection title='Related Tests'>
              <KeyDocumentPreparation
                entry={keyEntry}
                testId={testId}
                workspaceId={workspaceId}
                readOnly={readOnly}
                headerActions={!panelMode}
              />
            </DetailAsideSection>
          </DetailPanelAside>
        </DetailPanelLayout>
      </>
    )
  }

  if (!doc) {
    return (
      <DetailPanelLayout>
        <DetailPanelMain viewClassName='pt-0'>
          <div className='font-body text-muted-foreground py-12 text-center text-[12px]'>
            Document not found.
          </div>
        </DetailPanelMain>
      </DetailPanelLayout>
    )
  }

  const isUpload = doc.sourceType === 'UPLOADED' && doc.fileKey
  const hasContent = hasRenderablePolicyContent(doc.tiptapJson, doc.content)
  const isLocked = doc.isLocked
  const lockTooltip = formatLockTooltip(doc.lockedByTests)
  const showKeyDocumentEmptyState = Boolean(
    keyEntry && !versionId && !isEditing && !isUpload && !hasContent
  )

  const reviewHistory = !isEditing && <DocumentReviewHistory documentId={docId} workspaceId={workspaceId} readOnly={readOnly} versionId={versionId} />
  const documentActions = !readOnly && !panelMode && (
    <WorkspaceWriteGate workspaceId={workspaceId}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant='ghost'
            size='icon'
            className='h-8 w-8'
            aria-label='Document actions'
          >
            <MoreHorizontal className='h-4 w-4' />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align='end'>
          <DropdownMenuItem
            onSelect={() =>
              handleStatusChange(
                doc.status === 'ARCHIVED' ? 'ACTIVE' : 'ARCHIVED'
              )
            }
          >
            <Archive className='h-4 w-4' />
            {doc.status === 'ARCHIVED' ? 'Unarchive' : 'Archive'}
          </DropdownMenuItem>
          <DropdownMenuItem
            disabled={isLocked}
            onSelect={() => setShowDeleteDialog(true)}
            className='text-destructive'
          >
            <Trash2 className='h-4 w-4' />
            Delete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </WorkspaceWriteGate>
  )

  const editorActions = !readOnly && !panelMode && (
    <WorkspaceWriteGate workspaceId={workspaceId}>
      {!isEditing && !versionId && (!keyEntry || hasContent || isUpload) && (
        <LockableButton
          locked={isLocked}
          tooltip={lockTooltip}
          variant='outline'
          size='sm'
          className='border-border-divider h-8 rounded-sm px-3 text-[12px]'
          onClick={startEditing}
        >
          <Pencil className='h-3.5 w-3.5' />
          Edit
        </LockableButton>
      )}

      {isEditing && !isUpload && (
        <>
          <Button
            variant='outline'
            size='sm'
            className='border-border-divider h-8 rounded-sm px-3 text-[12px]'
            onClick={cancelEditing}
            disabled={updateMutation.isPending || replaceFileMutation.isPending || selectMaterialsMutation.isPending}
          >
            <X className='h-3.5 w-3.5' />
            Cancel
          </Button>
          <Button
            size='sm'
            className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm px-3 text-[12px]'
            onClick={handleSave}
            disabled={(!isUpload && !editor) || updateMutation.isPending || replaceFileMutation.isPending || selectMaterialsMutation.isPending}
          >
            <Save className='h-3.5 w-3.5' />
            {updateMutation.isPending || replaceFileMutation.isPending || selectMaterialsMutation.isPending ? 'Saving...' : 'Save'}
          </Button>
        </>
      )}
    </WorkspaceWriteGate>
  )

  const documentInformation = (inDrawer = false) => (
    <>
      <DetailAsideSection title='Document information'>
        <div className='w-full min-w-0 space-y-3'>
          <div>
            <p className='text-text-subtle text-[11px]'>Title</p>
            <div className='mt-1 space-y-2'>
              <p className='text-text-secondary text-[12px] font-medium break-words'>
                {doc.title}
              </p>
              {!!keyEntry?.frameworks?.length && (
                <div
                  className='flex min-w-0 flex-wrap gap-1'
                  aria-label={`Frameworks: ${keyEntry.frameworks.join(', ')}`}
                >
                  {[...new Set(keyEntry.frameworks)].map((framework) => (
                    <Badge
                      key={framework}
                      variant='outline'
                      className='border-cobalt-strong/20 bg-primary-subtle text-cobalt-strong rounded-sm px-1.5 py-0.5 text-[9px] font-medium'
                    >
                      {framework}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div>
            <p className='text-text-subtle text-[11px]'>Description</p>
            <p className='text-text-secondary mt-1 text-[12px] leading-5 break-words'>
              {doc.description || '—'}
            </p>
          </div>
          <MetadataRow
            label='Status'
            value={<DocumentStatusBadge status={doc.status} />}
          />
          <MetadataRow
            label='Category'
            value={VAULT_CATEGORY_LABELS[doc.category] ?? doc.category}
          />
          <MetadataRow
            label='Source'
            value={
              <span className='inline-flex max-w-full items-start gap-1.5'>
                {doc.sourceType === 'AGENT_GENERATED' && (
                  <Bot className='text-text-placeholder mt-0.5 h-3 w-3 shrink-0' />
                )}
                {doc.sourceType === 'UPLOADED' && (
                  <Upload className='text-text-placeholder mt-0.5 h-3 w-3 shrink-0' />
                )}
                <span className='min-w-0 break-words'>
                  {VAULT_SOURCE_LABELS[doc.sourceType] ?? doc.sourceType}
                </span>
              </span>
            }
          />
          <MetadataRow label='Created' value={formatDate(doc.createdAt)} />
          <MetadataRow label='Updated' value={formatDate(doc.updatedAt)} />
          {doc.effectiveFrom && (
            <MetadataRow
              label='Effective from'
              value={formatDate(doc.effectiveFrom)}
            />
          )}
          {doc.effectiveTo && (
            <MetadataRow
              label='Effective to'
              value={formatDate(doc.effectiveTo)}
            />
          )}
        </div>
      </DetailAsideSection>
      {keyEntry && !versionId && (
        <DetailAsideSection title='Related Tests'>
          <KeyDocumentPreparation
            key={`${keyEntry.key}:${docId}`}
            entry={keyEntry}
            testId={testId}
            workspaceId={workspaceId}
            documentId={docId}
            documentIds={documentIds}
            editing={isEditing}
            readOnly={readOnly}
            headerActions={!panelMode && !inDrawer}
            showActions={!inDrawer || panelMode}
            headerLeading={<>{reviewHistory}{editorActions}</>}
            headerTrailing={documentActions}
            showPreparationStatus={false}
          />
        </DetailAsideSection>
      )}
      {!keyEntry && doc.linkedTests.length > 0 && (
        <DetailAsideSection title='Related Tests'>
          <div className='w-full space-y-2'>
            {doc.linkedTests.map((test) => (
              <DocumentRelatedTest
                key={test.statusId}
                test={test}
                workspaceId={workspaceId}
                document={{
                  id: docId,
                  versionId: test.versionId,
                  revision: test.documentVersion,
                  currentRevision: currentVersion?.revision,
                  currentHasChanges: !!versionsQuery.data && !currentVersion,
                  linkToCurrent: !!versionId,
                }}
              />
            ))}
          </div>
        </DetailAsideSection>
      )}
      {(!keyEntry || hasContent || isUpload) && (
        <div className='w-full space-y-6'>
          {panelMode && <DetailAsideSection title='Review history'>{reviewHistory}</DetailAsideSection>}
          {!!versionsQuery.data?.length && (
            <DetailAsideSection title='Version history'>
              <SearchableSelect
                value={versionId ?? currentVersion?.id ?? 'draft'}
                options={[
                  ...(!currentVersion
                    ? [{ value: 'draft', label: 'Unversioned changes' }]
                    : []),
                  ...versionsQuery.data.map((version) => ({
                    value: version.id,
                    label: `Version ${version.revision}${version.isCurrent ? ' · Current' : ''}`,
                  })),
                ]}
                onValueChange={(v) =>
                  navigate({
                    to: '/workspaces/$workspaceId/documents/$docId',
                    params: { workspaceId, docId },
                    search: {
                      keyDocument: keyEntry?.key,
                      testId,
                      versionId:
                        v === 'draft' || v === currentVersion?.id
                          ? undefined
                          : v,
                    },
                  })
                }
                placeholder='Select a version...'
                searchPlaceholder='Search versions...'
                emptyMessage='No versions found.'
                triggerProps={{
                  className: 'w-full justify-start font-normal',
                }}
                // Version ids are opaque and must not affect visible results.
                contentProps={{
                  filter: (_value, search, keywords) =>
                    (keywords ?? [])
                      .join(' ')
                      .toLowerCase()
                      .includes(search.trim().toLowerCase())
                      ? 1
                      : 0,
                }}
              />
            </DetailAsideSection>
          )}
          {doc.associatedControls?.length > 0 && (
            <DetailAsideSection title='Associated Controls'>
              <FrameworkRefChips
                refs={[
                  ...new Set(
                    doc.associatedControls.map((control) => control.framework)
                  ),
                ].map((framework) => ({
                  framework,
                  refs: doc.associatedControls
                    .filter((control) => control.framework === framework)
                    .map((control) => control.ref),
                }))}
                className='mt-0'
              />
            </DetailAsideSection>
          )}

          {suggestedTests.length > 0 && (
            <DetailAsideSection title='Suggested Tests'>
              <div className='flex w-full flex-col gap-1'>
                {suggestedTests.map((suggestion) => (
                  <SuggestedTestRow
                    key={suggestion.statusId}
                    suggestion={suggestion}
                    workspaceId={workspaceId}
                    readOnly={readOnly}
                    onDismiss={() =>
                      handleDismissSuggestion(suggestion.statusId)
                    }
                  />
                ))}
              </div>
            </DetailAsideSection>
          )}

          {doc.tags?.length > 0 && (
            <DetailAsideSection title='Tags'>
              <div className='flex w-full flex-wrap gap-1.5'>
                {doc.tags?.map((tag: string) => (
                  <span
                    key={tag}
                    className='bg-surface-subtle text-muted-foreground border-border-soft dark:border-border inline-flex items-center rounded-sm border px-2 py-0.5 text-[10px]'
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </DetailAsideSection>
          )}
        </div>
      )}
    </>
  )

  return (
    <>
      {!panelMode && (
        <PageHeaderBreadcrumb>
          {documentBreadcrumb}
        </PageHeaderBreadcrumb>
      )}
      {!panelMode && !readOnly && !versionId && (
        <PageHeaderExtra>
          <LockableButton
            locked={isLocked}
            tooltip={lockTooltip}
            actionTooltip='Edit document details'
            variant='ghost'
            size='icon'
            aria-label='Edit document details'
            className='h-7 w-7 rounded-sm text-text-subtle hover:bg-muted hover:text-text-body'
            onClick={startEditingDetails}
            disabled={isEditing || updateMutation.isPending}
          >
            <Pencil className='h-3.5 w-3.5' />
          </LockableButton>
        </PageHeaderExtra>
      )}
      {(!keyEntry || versionId) && !panelMode && (
        <PageHeaderActions>{reviewHistory}{editorActions}{documentActions}</PageHeaderActions>
      )}
      <DetailPanelLayout
        className={panelMode ? 'h-full max-h-full' : undefined}
      >
        <DetailPanelMain
          className={showKeyDocumentEmptyState ? 'bg-surface-subtle' : undefined}
          viewClassName={
            showKeyDocumentEmptyState
              ? 'px-0 pt-0 pb-0'
              : isUpload
                ? 'pt-0 [&>div]:h-full'
                : 'pt-0'
          }
          contentClassName={
            showKeyDocumentEmptyState ? 'h-full max-w-none pb-0' : undefined
          }
        >
          <Sheet>
            <div className='pt-4 lg:hidden'>
              <SheetTrigger asChild>
                <Button variant='outline' size='sm'>
                  <FileText className='h-3.5 w-3.5' />
                  Document information
                </Button>
              </SheetTrigger>
            </div>
            <SheetContent
              className='w-full gap-0 sm:max-w-sm'
              aria-describedby={undefined}
            >
              <SheetHeader>
                <SheetTitle>Document information</SheetTitle>
              </SheetHeader>
              <div className='min-h-0 flex-1 space-y-6 overflow-y-auto p-4 [&_section>div]:min-w-0'>
                {documentInformation(true)}
              </div>
            </SheetContent>
          </Sheet>
          {/* Main content area */}
          <div
            className={
              showKeyDocumentEmptyState
                ? 'h-full min-w-0'
                : isUpload
                  ? panelMode
                    ? 'flex h-full min-h-0 min-w-0 flex-col pt-8'
                    : 'flex h-full min-h-0 min-w-0 flex-col pt-6'
                  : panelMode
                    ? 'min-w-0 pt-8'
                    : 'min-w-0 pt-6 h-full'
            }
          >
            {keyEntry && !versionId && (
              <KeyDocumentSources
                entry={keyEntry}
                testId={testId}
                workspaceId={workspaceId}
                documentId={docId}
                documentIds={documentIds}
                readOnly={readOnly}
                editing={isEditing}
                empty={!hasContent && !isUpload}
              />
            )}
            {isEditing && !readOnly && !isUpload ? (
              <div className='space-y-5'>
                <VaultDocumentEditor
                  title={doc.title}
                  category={doc.category}
                  description={doc.description ?? ''}
                  contentMarkdown={doc.content}
                  tiptapJson={doc.tiptapJson}
                  contentKey={`vault-doc-edit-${doc.id}`}
                  variant='split'
                  hideMetaForm
                  className='mx-auto flex w-full max-w-[816px] flex-col gap-5 [&_.editor-container]:pt-0'
                  onEditorReady={(nextEditor) =>
                    handleEditorCreate({ editor: nextEditor })
                  }
                  editorToolbar={
                    editor ? (
                      <EditorContext.Provider
                        value={{ editor } as EditorContextValue}
                      >
                        <div className='min-w-0 flex-1 [&_.tiptap-toolbar]:static [&_.tiptap-toolbar]:!min-h-9 [&_.tiptap-toolbar]:!border-0 [&_.tiptap-toolbar]:!bg-transparent [&_.tiptap-toolbar]:px-0'>
                          <MainToolbar editor={editor} />
                        </div>
                      </EditorContext.Provider>
                    ) : (
                      <div className='h-9' />
                    )
                  }
                />
              </div>
            ) : isUpload ? (
              // File upload view — render actual file content.
              <div className='mx-auto flex min-h-0 w-full max-w-[816px] flex-1 flex-col'>
                <UploadedFileViewer
                  docId={doc.id}
                  fileName={doc.fileName}
                  fileMimeType={doc.fileMimeType}
                  versionId={versionId}
                />
                {hasContent && (
                  <div className='bg-card mt-4 rounded-sm border p-6'>
                    <h3 className='mb-3 font-medium'>Supporting notes</h3>
                    <PolicyContentViewer
                      contentMarkdown={doc.content}
                      plateContent={doc.tiptapJson}
                      readOnly
                      contentKey={`vault-notes-${doc.id}-${versionId ?? 'draft'}`}
                    />
                  </div>
                )}
              </div>
            ) : hasContent ? (
              // Rich content view (read-only TipTap)
              <div className='mx-auto w-full max-w-[816px] min-w-0'>
                <div className='border-border rounded-sm border bg-card px-15 py-8'>
                  <PolicyContentViewer
                    contentMarkdown={doc.content}
                    plateContent={doc.tiptapJson}
                    readOnly
                    contentKey={`vault-doc-${doc.id}-${versionId ?? 'draft'}`}
                    onCreate={handleEditorCreate}
                    className='min-h-[300px]'
                  />
                </div>
              </div>
            ) : keyEntry ? null : (
              // Empty document — prompt to add content
              <div className='border-border-divider rounded-sm border border-dashed bg-card p-8 text-center'>
                <FileText className='text-text-disabled mx-auto mb-3 h-10 w-10' />
                <p className='font-body text-text-body text-[13px] font-medium'>
                  No content yet
                </p>
                <p className='font-body text-muted-foreground mt-1 text-[12px]'>
                  Click Edit to add content to this document.
                </p>
                {!readOnly && (
                  <Button
                    size='sm'
                    className='bg-primary text-primary-foreground hover:bg-primary/90 mt-4 h-8 rounded-sm px-4 text-[12px]'
                    onClick={startEditing}
                  >
                    <Pencil className='h-3.5 w-3.5' />
                    Add Content
                  </Button>
                )}
              </div>
            )}
          </div>
        </DetailPanelMain>

        {/* Metadata sidebar */}
        <DetailPanelAside className='bg-card [&_section>div]:min-w-0'>
          {documentInformation()}
        </DetailPanelAside>
      </DetailPanelLayout>
      <Dialog
        open={Boolean(isUpload && isEditing)}
        onOpenChange={(open) => {
          if (
            !open &&
            !updateMutation.isPending &&
            !replaceFileMutation.isPending &&
            !selectMaterialsMutation.isPending
          ) {
            cancelEditing()
          }
        }}
      >
        <DialogContent className='sm:max-w-[720px]'>
          <DialogHeader>
            <DialogTitle>Replace document file</DialogTitle>
          </DialogHeader>
          <div className='space-y-5 px-6'>
            <div className='space-y-3'>
              <div>
                <Label className='text-[13px] font-medium'>Replace file</Label>
                <p className='text-muted-foreground mt-1 text-[13px] leading-5'>
                  Save a replacement as a new version. Previous versions and
                  submitted evidence are preserved.
                </p>
              </div>
              <UploadFile
                id='vault-replace-file'
                file={replacementFile}
                acceptedFormats={UPLOAD_ACCEPT}
                supportedFormats='PDF, Word, Excel, CSV, Text, Images up to 10 MB'
                handleFileChange={(event) => {
                  const file = event.target.files?.[0] ?? null
                  if (file && file.size > MAX_FILE_SIZE) {
                    toast.error('Maximum file size is 10 MB.')
                    event.target.value = ''
                    setReplacementFile(null)
                    return
                  }
                  setReplacementFile(file)
                }}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              className='border-border-divider h-8 rounded-sm px-3 text-[12px]'
              onClick={cancelEditing}
              disabled={
                updateMutation.isPending ||
                replaceFileMutation.isPending ||
                selectMaterialsMutation.isPending
              }
            >
              Cancel
            </Button>
            <Button
              size='sm'
              className='bg-primary text-primary-foreground hover:bg-primary/90 h-8 rounded-sm px-3 text-[12px]'
              onClick={handleSave}
              disabled={
                !replacementFile ||
                updateMutation.isPending ||
                replaceFileMutation.isPending ||
                selectMaterialsMutation.isPending
              }
            >
              {updateMutation.isPending ||
              replaceFileMutation.isPending ||
              selectMaterialsMutation.isPending
                ? 'Saving...'
                : 'Replace file'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <Dialog
        open={showDetailsDialog}
        onOpenChange={(open) => {
          if (!updateMutation.isPending) setShowDetailsDialog(open)
        }}
      >
        <DialogContent className='sm:max-w-[560px]'>
          <DialogHeader>
            <DialogTitle>Edit document details</DialogTitle>
          </DialogHeader>
          <div className='space-y-4 px-6'>
            <div className='space-y-1.5'>
              <Label htmlFor='vault-document-title'>Title</Label>
              <Input
                id='vault-document-title'
                value={editTitle}
                onChange={(event) => setEditTitle(event.target.value)}
                placeholder='Document title'
              />
            </div>
            <div className='space-y-1.5'>
              <Label>Category</Label>
              <Select
                value={editCategory}
                onValueChange={(value) =>
                  setEditCategory(value as VaultDocumentCategory)
                }
              >
                <SelectTrigger fullWidth>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(VAULT_CATEGORY_LABELS).map(
                    ([value, label]) => (
                      <SelectItem key={value} value={value}>
                        {label}
                      </SelectItem>
                    )
                  )}
                </SelectContent>
              </Select>
            </div>
            <div className='space-y-1.5'>
              <Label htmlFor='vault-document-description'>Description</Label>
              <Textarea
                id='vault-document-description'
                value={editDescription}
                onChange={(event) => setEditDescription(event.target.value)}
                className='min-h-24 resize-y'
                placeholder='Brief description'
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant='outline'
              onClick={() => setShowDetailsDialog(false)}
              disabled={updateMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              onClick={handleDetailsSave}
              disabled={!editTitle.trim() || updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ImageEditorDialogHost analyticsSource='vault_document' />
      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className='sm:max-w-[400px]'>
          <DialogHeader>
            <DialogTitle className='font-heading text-[16px]'>
              Delete Document
            </DialogTitle>
          </DialogHeader>
          <p className='font-body text-text-body px-6 text-[13px]'>
            Are you sure you want to delete "{doc.title}"? This action cannot be
            undone.
          </p>
          <DialogFooter>
            <Button
              variant='outline'
              size='sm'
              className='border-border-divider h-8 rounded-sm text-[12px]'
              onClick={() => setShowDeleteDialog(false)}
            >
              Cancel
            </Button>
            <Button
              size='sm'
              className='h-8 rounded-sm bg-red-600 text-[12px] text-white hover:bg-red-700'
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

// ── Metadata Row ──

function MetadataRow({
  label,
  value,
}: {
  label: string
  value: React.ReactNode
}) {
  return (
    <div className='flex items-start justify-between gap-3'>
      <span className='text-text-subtle shrink-0 text-[11px]'>{label}</span>
      <span className='text-text-secondary min-w-0 text-right text-[11px] break-words tabular-nums'>
        {value}
      </span>
    </div>
  )
}

// ── Lockable action button ──
// Wraps a Button in a Tooltip when `locked`; clicks no-op and aria-disabled
// flips on. We don't use `disabled` so the tooltip stays interactive.
function LockableButton({
  locked,
  tooltip,
  actionTooltip,
  onClick,
  className,
  children,
  ...rest
}: React.ComponentProps<typeof Button> & {
  locked: boolean
  tooltip: string
  actionTooltip?: string
}) {
  const button = (
    <Button
      {...rest}
      tooltip={locked ? undefined : actionTooltip}
      aria-disabled={locked || undefined}
      className={`${className ?? ''} ${locked ? 'cursor-not-allowed opacity-50' : ''}`}
      onClick={(e) => {
        if (locked) {
          e.preventDefault()
          e.stopPropagation()
          return
        }
        onClick?.(e)
      }}
    >
      {children}
    </Button>
  )
  if (!locked) return button
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <span tabIndex={0}>{button}</span>
      </TooltipTrigger>
      <TooltipContent side='bottom' className='max-w-[280px] text-[11px]'>
        {tooltip}
      </TooltipContent>
    </Tooltip>
  )
}

// ── Suggested Tests sidebar row ──
const SUGGESTION_CONFIDENCE_BADGE: Record<
  VaultDocSuggestedTestDTO['confidence'],
  { label: string; className: string }
> = {
  high: {
    label: 'High',
    className: 'border-status-green/30 bg-status-green-bg text-status-green',
  },
  medium: {
    label: 'Medium',
    className: 'border-[#EAD9A8] bg-[#FBF3DC] text-[#8A6D1F]',
  },
  low: {
    label: 'Low',
    className: 'border-border-surface bg-muted text-muted-foreground',
  },
}

function SuggestedTestRow({
  suggestion,
  workspaceId,
  readOnly,
  onDismiss,
}: {
  suggestion: VaultDocSuggestedTestDTO
  workspaceId: string
  readOnly: boolean
  onDismiss: () => void
}) {
  const badge = SUGGESTION_CONFIDENCE_BADGE[suggestion.confidence]
  const row = (
    <div className='group hover:border-border hover:bg-surface-subtle flex items-center justify-between gap-2 rounded-sm border border-transparent px-2 py-1.5 transition-colors'>
      <Link
        to='/workspaces/$workspaceId/tests/$testId'
        params={{ workspaceId, testId: suggestion.statusId }}
        className='flex min-w-0 items-center gap-1.5'
      >
        {suggestion.taskCode && (
          <span className='shrink-0 text-[10px] font-semibold tracking-wide text-cobalt-strong tabular-nums'>
            TEST-{suggestion.taskCode}
          </span>
        )}
        <span className='text-text-secondary min-w-0 truncate text-[11px] font-medium hover:text-cobalt-strong'>
          {suggestion.title}
        </span>
      </Link>
      <div className='flex shrink-0 items-center gap-1'>
        <span
          className={`inline-flex h-4 items-center rounded-sm border px-1.5 text-[9px] font-medium ${badge.className}`}
        >
          {badge.label}
        </span>
        {!readOnly && (
          <button
            type='button'
            aria-label='Dismiss suggestion'
            className='text-text-disabled hover:text-muted-foreground rounded-sm p-0.5 opacity-0 transition-opacity group-hover:opacity-100'
            onClick={onDismiss}
          >
            <X className='h-3 w-3' />
          </button>
        )}
      </div>
    </div>
  )
  if (!suggestion.reasoning) return row
  return (
    <Tooltip>
      <TooltipTrigger asChild>{row}</TooltipTrigger>
      <TooltipContent side='left' className='max-w-[280px] text-[11px]'>
        {suggestion.reasoning}
      </TooltipContent>
    </Tooltip>
  )
}
